/**
 * The pieces every detail panel is built from.
 *
 * ── Why these are here and not in `src/components/ui.tsx` ────────────────────
 *
 * `ui.tsx`'s `Collapsible`, `usePaged` and `Pager` render class names —
 * `.fold`, `.fold-head`, `.pager` — that `index.css` does not currently
 * contain. §10.3 notes that; the CSS belongs to another work unit in this
 * build, and two agents editing one stylesheet is how a rule disappears
 * without anybody noticing. So the detail panels style themselves inline
 * against the tokens `index.css` already defines, exactly as `Intake.tsx` and
 * `MapScreen.tsx` do, and every colour is a `var(--…)` so both themes work.
 *
 * ── The one design rule these encode ─────────────────────────────────────────
 *
 * A number on this screen is about to be said out loud to a seller. So the
 * primitives make it HARD to render a value without saying where it came
 * from: `Fact` takes a certainty and will not render without one, `Money`
 * takes an `assumed` flag, and `Panel` takes a tone so a panel that is not
 * fully verified cannot look the same as one that is.
 */

import { useCallback, useState, type CSSProperties, type ReactNode } from 'react'
import Icon, { type IconName } from '../Icon'
import { certaintyBadge, type Tone } from '../../lib/format'
import type { Certainty } from '../../../shared/listing'
import type { Locale } from '../../lib/i18n'

/* ────────────────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────────────── */

export const S = {
  page: { padding: 12, paddingBottom: 96, maxWidth: 720, margin: '0 auto' } as CSSProperties,

  panel: {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--radius)',
    marginBottom: 10,
    overflow: 'hidden',
  } as CSSProperties,
  panelHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minHeight: 52,
    padding: '0 12px',
    background: 'transparent',
    border: 'none',
    color: 'var(--ink)',
    font: 'inherit',
    fontSize: 15,
    fontWeight: 700,
    textAlign: 'left',
    cursor: 'pointer',
  } as CSSProperties,
  panelBody: { padding: '2px 12px 14px' } as CSSProperties,

  row: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    padding: '7px 0',
    borderTop: '1px solid var(--line)',
  } as CSSProperties,
  rowFirst: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    padding: '7px 0',
  } as CSSProperties,
  label: { fontSize: 13, color: 'var(--muted)', flex: '0 1 auto' } as CSSProperties,
  value: { fontSize: 15, fontWeight: 600, textAlign: 'right', flex: '1 1 auto' } as CSSProperties,

  note: { fontSize: 13, lineHeight: 1.45, color: 'var(--muted)', margin: '6px 0 0' } as CSSProperties,
  sentence: { fontSize: 14, lineHeight: 1.5, margin: '4px 0 0' } as CSSProperties,

  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' } as CSSProperties,

  big: { fontSize: 24, fontWeight: 800, letterSpacing: '-0.01em' } as CSSProperties,
  huge: { fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em' } as CSSProperties,

  button: {
    minHeight: 48,
    padding: '0 14px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--line)',
    background: 'var(--bg)',
    color: 'var(--ink)',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  } as CSSProperties,
} as const

/** A tone as inline style. Filled by default; outlined for anything unverified. */
export function toneStyle(tone: Tone, outlined = false): CSSProperties {
  return outlined
    ? { color: tone.fg, background: 'transparent', border: `1px dashed ${tone.line === 'transparent' ? tone.fg : tone.line}` }
    : { color: tone.fg, background: tone.bg, border: `1px solid ${tone.line}` }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Chips and badges
 * ────────────────────────────────────────────────────────────────────────── */

export function Chip({
  tone,
  outlined = false,
  title,
  icon,
  children,
}: {
  tone: Tone
  outlined?: boolean
  title?: string
  icon?: IconName
  children: ReactNode
}) {
  return (
    <span
      title={title}
      style={{
        ...toneStyle(tone, outlined),
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 999,
        padding: '3px 9px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  )
}

/**
 * Where a value came from, in three words.
 *
 * **An AI inference must never look like a confirmed fact.** It gets the
 * danger tone and a dashed outline, and it is never abbreviated to something
 * that could be mistaken for an estimate we made deliberately.
 */
export function CertaintyChip({ certainty, locale }: { certainty: Certainty; locale: Locale }) {
  const badge = certaintyBadge(certainty, locale)
  return (
    <Chip tone={badge.tone} outlined={badge.outlined} title={badge.hint}>
      {badge.label}
    </Chip>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * Panels
 * ────────────────────────────────────────────────────────────────────────── */

/** Remembered per panel, per browser. Never throws: private windows deny access. */
function useRemembered(key: string, fallback: boolean): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const held = localStorage.getItem(key)
      return held === null ? fallback : held === '1'
    } catch {
      return fallback
    }
  })
  const set = useCallback(
    (v: boolean) => {
      setValue(v)
      try {
        localStorage.setItem(key, v ? '1' : '0')
      } catch {
        /* the preference simply will not persist */
      }
    },
    [key],
  )
  return [value, set]
}

/**
 * A folding section.
 *
 * Closed panels are UNMOUNTED, not hidden: this screen carries ten panels and
 * a folded one should cost nothing to have on the page — which on a phone in
 * a field is the difference between scrolling and waiting.
 */
export function Panel({
  id,
  title,
  badge,
  defaultOpen = false,
  children,
}: {
  id: string
  title: string
  /** A chip in the header — a count, a band, an access level. */
  badge?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useRemembered(`hh.detail.${id}`, defaultOpen)
  return (
    <section style={S.panel}>
      <button type="button" style={S.panelHead} onClick={() => setOpen(!open)} aria-expanded={open}>
        {/* Rotated inline: `index.css` belongs to another unit in this build,
            so a class name here would have nothing behind it. */}
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 120ms ease',
            color: 'var(--muted)',
          }}
        >
          <Icon name="chevron" size={16} />
        </span>
        <span style={{ flex: 1 }}>{title}</span>
        {badge}
      </button>
      {open && <div style={S.panelBody}>{children}</div>}
    </section>
  )
}

/** A label and a value, on one line. The detail screen's atom. */
export function Line({
  label,
  children,
  first = false,
  after,
}: {
  label: string
  children: ReactNode
  first?: boolean
  /** Chips that qualify the value — a certainty, an "assumed". */
  after?: ReactNode
}) {
  return (
    <div style={first ? S.rowFirst : S.row}>
      <span style={S.label}>{label}</span>
      <span style={S.value}>
        {children}
        {after && <span style={{ marginLeft: 6, display: 'inline-flex', gap: 4, verticalAlign: 'middle' }}>{after}</span>}
      </span>
    </div>
  )
}

/** A 0–100 meter. Used for factor sub-scores, where the bar is read before the number. */
export function Meter({ value, tone }: { value: number | null; tone: Tone }) {
  const width = value === null ? 0 : Math.max(0, Math.min(100, value))
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        height: 4,
        borderRadius: 999,
        background: 'var(--line)',
        overflow: 'hidden',
        marginTop: 6,
      }}
    >
      <span style={{ display: 'block', height: '100%', width: `${width}%`, background: tone.fg }} />
    </span>
  )
}

export function Banner({
  tone,
  icon,
  children,
  outlined = false,
}: {
  tone: Tone
  icon?: IconName
  children: ReactNode
  outlined?: boolean
}) {
  return (
    <div
      role="status"
      style={{
        ...toneStyle(tone, outlined),
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        padding: '10px 12px',
        borderRadius: 'var(--radius)',
        fontSize: 14,
        lineHeight: 1.45,
        marginBottom: 10,
      }}
    >
      {icon && (
        <span style={{ flex: '0 0 auto', marginTop: 1 }}>
          <Icon name={icon} size={18} />
        </span>
      )}
      <span>{children}</span>
    </div>
  )
}

/** What a panel says when it has nothing — never a blank body. */
export function Empty({ children }: { children: ReactNode }) {
  return <p style={{ ...S.note, margin: '8px 0 2px' }}>{children}</p>
}
